import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaFacebookF, FaLinkedinIn, FaInstagram, FaXTwitter } from "react-icons/fa6";
import "../styles/Home.scss";

// ✅ EXISTING VIDEO (NO RENAME, NO MOVE)
import solarVideo from "../assets/video/Solar Plant Shot home .mp4";

const Hero = () => {
  const videoRef = useRef(null);
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTypingComplete, setIsTypingComplete] = useState(false);

  const textLine = "A DECADE OF SOLAR STRENGTH A FUTURE POWERED BY STORAGE";
  const subText = "2 GW+ Solar EPC/IPP & 1 GWh+ BESS Solutions | 10+ Years of Trusted Excellence";

  useEffect(() => {
    AOS.init({ duration: 1000, once: true, easing: 'ease-out-cubic' });
  }, []);

  // Optimized typing effect with better performance
  useEffect(() => {
    if (currentIndex < textLine.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(textLine.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, 80 + Math.random() * 40); // Slightly slower for smoother feel

      return () => clearTimeout(timeout);
    } else {
      setIsTypingComplete(true);
    }
  }, [currentIndex, textLine]);

  // Optimized render with better performance
  const renderTypingText = () => {
    const words = displayedText.split(' ');
    let charIndex = 0;

    return words.map((word, wordIndex) => {
      const wordStartIndex = charIndex;
      const wordChars = word.split('').map((char, charIndexInWord) => {
        const globalCharIndex = wordStartIndex + charIndexInWord;
        return (
          <motion.span
            key={`${wordIndex}-${charIndexInWord}`}
            initial={{ opacity: 0, y: 15, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              duration: 0.3,
              ease: "easeOut",
              delay: globalCharIndex * 0.04
            }}
            style={{
              display: 'inline-block',
              willChange: 'transform, opacity'
            }}
          >
            {char}
          </motion.span>
        );
      });

      charIndex += word.length + 1; // +1 for the space

      return (
        <motion.span
          key={wordIndex}
          className="d-inline-block"
          style={{
            whiteSpace: 'nowrap',
            marginRight: '0.5rem',
            willChange: 'transform'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 0.2,
            delay: wordStartIndex * 0.04
          }}
        >
          {wordChars}
        </motion.span>
      );
    });
  };

  // Animation variants for Framer Motion
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.03,
        delayChildren: 0.5
      }
    }
  };

  const letterVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.8
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 200
      }
    }
  };

  const cursorVariants = {
    blinking: {
      opacity: [1, 0, 1],
      transition: {
        duration: 1.2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    },
    complete: {
      opacity: [1, 0.3, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const socialIconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        damping: 10,
        stiffness: 100,
        delay: 0.1
      }
    },
    hover: {
      scale: 1.15,
      y: -3,
      transition: {
        type: "spring",
        damping: 10,
        stiffness: 400
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Rama Reflection India Pvt Ltd | Solar Energy Solutions</title>
        <meta
          name="description"
          content="2 GW+ Solar EPC/IPP & 1 GWh+ BESS Solutions | 10+ Years of Trusted Excellence"
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin />
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      {/* Hero Container with Bootstrap Classes */}
      <div className="hero-container position-relative w-100 vh-100 overflow-hidden text-white" style={{ minHeight: '600px' }}>
        {/* Background Video */}
        <video
          ref={videoRef}
          className="hero-video position-absolute top-0 start-0 w-100 h-100"
          style={{ zIndex: -2 }}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        >
          <source src={solarVideo} type="video/mp4" />
        </video>

        {/* Overlay */}
        <div className="overlay position-absolute top-0 start-0 w-100 h-100" style={{ zIndex: -1 }}></div>

        {/* Bootstrap Container for Perfect Centering */}
        <div className="container-fluid h-100">
          <div className="row h-100 justify-content-center align-items-center">
            <div className="col-12 col-lg-10 col-xl-9 col-xxl-7">
              {/* Text Box - Centered with Framer Motion */}
              <motion.div
                className="text-center position-relative px-3"
                style={{ zIndex: 1 }}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <motion.h1
                  className="hero-heading fw-bold mb-3"
                  style={{
                    lineHeight: '1.2',
                    wordBreak: 'normal',
                    fontSize: 'clamp(2.4rem, 3vw, 3rem)',
                    willChange: 'transform, opacity'
                  }}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                >
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '0.3rem',
                    transform: 'translateZ(0)', // Force hardware acceleration
                    backfaceVisibility: 'hidden' // Reduce flickering
                  }}>
                    {renderTypingText()}
                  </div>
                </motion.h1>

                <motion.p
                  className="hero-subtext mb-0 fw-light lh-base fs-5 fs-md-4 opacity-75"
                  style={{ color: '#f1f1f1' }}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.5 }}
                >
                  {subText}
                </motion.p>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Social Icons with Bootstrap Classes */}
        <motion.div
          className="position-absolute d-flex gap-3 d-none d-sm-flex"
          style={{
            bottom: '30px',
            right: '30px',
            zIndex: 2
          }}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 2 }}
        >
          {[
            { href: "https://www.facebook.com/share/17dmurs9AK/", icon: FaFacebookF, delay: 0 },
            { href: "https://www.linkedin.com/company/rama-reflection-2010/", icon: FaLinkedinIn, delay: 0.1 },
            { href: "https://www.instagram.com/ramareflection2010", icon: FaInstagram, delay: 0.2 },
            { href: "https://twitter.com/RamaReflection", icon: FaXTwitter, delay: 0.3 }
          ].map(({ href, icon: Icon, delay }, index) => (
            <motion.a
              key={index}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="social-link d-flex align-items-center justify-content-center rounded-circle text-white text-decoration-none position-relative overflow-hidden border border-opacity-25"
              style={{
                width: '50px',
                height: '50px',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderColor: 'rgba(255, 255, 255, 0.2)'
              }}
              variants={socialIconVariants}
              initial="hidden"
              animate="visible"
              whileHover="hover"
              whileTap={{ scale: 0.95 }}
              transition={{ delay }}
            >
              <Icon size={20} />
            </motion.a>
          ))}
        </motion.div>

        {/* Mobile Social Icons */}
        <motion.div
          className="position-absolute d-flex gap-2 d-sm-none"
          style={{
            bottom: '20px',
            right: '20px',
            zIndex: 2
          }}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 2 }}
        >
          {[
            { href: "https://www.facebook.com/share/17dmurs9AK/", icon: FaFacebookF },
            { href: "https://www.linkedin.com/company/rama-reflection-2010/", icon: FaLinkedinIn },
            { href: "https://www.instagram.com/ramareflection2010", icon: FaInstagram },
            { href: "https://twitter.com/RamaReflection", icon: FaXTwitter }
          ].map(({ href, icon: Icon }, index) => (
            <motion.a
              key={index}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="social-link d-flex align-items-center justify-content-center rounded-circle text-white text-decoration-none position-relative overflow-hidden border border-opacity-25"
              style={{
                width: '40px',
                height: '40px',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderColor: 'rgba(255, 255, 255, 0.2)'
              }}
              variants={socialIconVariants}
              initial="hidden"
              animate="visible"
              whileHover="hover"
              whileTap={{ scale: 0.95 }}
              transition={{ delay: index * 0.1 }}
            >
              <Icon size={16} />
            </motion.a>
          ))}
        </motion.div>
      </div>
    </>
  );
};

export default Hero;